import { proxy, subscribe } from 'valtio';
import { z } from 'zod';

const stateKey = `global-state-${import.meta.env.DEV ? 'dev' : __BUILD_ID__}`;

const shouldEnablePreprodFromQuery = (() => {
  if (typeof window === 'undefined') return false;

  try {
    if (
      globalThis.location.hostname === 'kundeportal.kommunekredit.dk' ||
      globalThis.location.hostname === 'mit.kommunekredit.dk'
    ) {
      return false;
    }
    return (
      new URLSearchParams(window.location.search).get('preprod') === 'true'
    );
  } catch (_error) {
    return false;
  }
})();

const stateSchema = z.object({
  devtoolsEnabled: z.boolean(),
  devtoolsUseProdFlags: z.boolean(),
});

type State = z.infer<typeof stateSchema>;

const initial: State = {
  devtoolsEnabled: false,
  devtoolsUseProdFlags: false,
};

const cached =
  import.meta.env.MODE === 'test'
    ? null
    : globalThis.localStorage.getItem(stateKey);

const persistedState = stateSchema.safeParse(
  cached ? JSON.parse(cached) || initial : initial,
);

const baseState = persistedState.success ? persistedState.data : initial;

const state = proxy<State>(baseState);

if (import.meta.env.MODE !== 'test') {
  subscribe(state, () => {
    globalThis.requestIdleCallback(
      () => {
        globalThis.localStorage.setItem(stateKey, JSON.stringify(state));
      },
      { timeout: 1000 },
    );
  });
}

if (shouldEnablePreprodFromQuery) {
  if (!state.devtoolsEnabled) {
    state.devtoolsEnabled = true;
  }
  if (!state.devtoolsUseProdFlags) {
    state.devtoolsUseProdFlags = true;
  }
}

/** Toggles the devtoolsEnabled state */
function toggleDevtools() {
  state.devtoolsEnabled = !state.devtoolsEnabled;
}

Object.defineProperty(globalThis, 'devtoolsEnabled', {
  get: () => state.devtoolsEnabled,
  set: (value) => {
    state.devtoolsEnabled = value;
  },
});

globalThis.toggleDevtools = toggleDevtools;

export default state;
